import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useState, useEffect, useRef } from 'react';
import { axiosRequest } from '../utils/axiosRequest';
import { IUser, IUserDocumentTemplate, IOrganization } from '@homeporter/types';
import { IPlan, IProfile, IUserSettings } from '../global/interfaces';
import { convertCardType } from '../utils/helpers';
import {
  getFormattedTimezone,
  applyApplicationFontSize,
} from '../utils/helpers';

const useUserProfile = () => {
  const axios = axiosRequest();
  const queryClient = useQueryClient();

  const [gitHash, setGitHash] = useState<string>('');
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [services, setServices] = useState<number[]>([]);
  const [documentTemplates, setDocumentTemplates] = useState<
    IUserDocumentTemplate[]
  >([]);

  const [organization, setOrganization] = useState<IOrganization>({
    name: '',
  });

  const [userSettings, setUserSettings] = useState<IUserSettings>({
    fontSize: 100,
    dev: false,
    supervisor: false,
    timezone: getFormattedTimezone(),
    bookingLeadTime: 4,
    bookingAvailabilityWindow: 14,
    availability: [],
    blockTime: [],
  });

  const [profile, setProfile] = useState<IProfile>({
    user_id: 0,
    organization_id: 0,
    firstname: '',
    lastname: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });

  const fetchUserProfile = async () => {
    console.log('::::fetchUserProfile...');
    try {
      const { data } = await axios.get('/users/profile');

      console.log('::::fetchUserProfile data:', data);

      const {
        plans,
        services,
        documentTemplates,
        settings,
        organization,
        ...restProfile
      } = data.data ?? {};

      setGitHash(data.git || '');
      setProfile(restProfile);

      if (documentTemplates) {
        const updatedDocumentTemplates = documentTemplates.map(
          (template: any) => {
            return {
              ...template,
              sections: template.sections.map((section: any) => ({
                ...section,
                cards: section.cards.map(convertCardType),
              })),
            };
          },
        );

        setDocumentTemplates(updatedDocumentTemplates);
      }

      if (plans) {
        setPlans(plans);
      }

      if (services) {
        setServices(services);
      }

      if (settings) {
        setUserSettings({ ...userSettings, ...settings });
        if (settings.fontSize) {
          applyApplicationFontSize(settings.fontSize);
        }
      }

      if (organization) {
        setOrganization(organization);
      }

      return data.data;
    } catch (error) {
      console.log('::::fetchUserProfile... error:', error);
      return null;
    }
  };

  useEffect(() => {
    console.log('::::useEffect gitHash...', gitHash);
  }, [gitHash]);

  useEffect(() => {
    console.log('::::useEffect profile...', profile);
  }, [profile]);

  useEffect(() => {
    console.log('::::useEffect plans...', plans);
  }, [plans]);

  useEffect(() => {
    console.log('::::useEffect services...', services);
  }, [services]);

  useEffect(() => {
    console.log('::::useEffect documentTemplates...', documentTemplates);
  }, [documentTemplates]);

  useEffect(() => {
    console.log('::::useEffect organization...', organization);
  }, [organization]);

  useEffect(() => {
    console.log('::::useEffect userSettings...', userSettings);
  }, [userSettings]);

  const { data, isLoading, isError } = useQuery(
    ['userProfile'],
    fetchUserProfile,
    {
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const { mutateAsync: updateProfile, error: updateProfileError } = useMutation(
    async (payload: Record<string, any>) => {
      console.log('::::updateProfile payload:', payload);

      const response = await axios.put('users/profile', payload);

      console.log('::::updateProfile response:', response);

      if (response.data.status === 'success') {
        queryClient.invalidateQueries('userProfile');

        return response.data;
      } else {
        throw new Error('An error occurred while updating user details');
      }
    },
  );
  /*
  const createDocumentTemplate = useMutation(
    async (payload: Record<string, any>) => {
      if (!profile?.user_id) throw new Error('No user profile available');

      const { data } = await axios.post(
        `users/document_templates/${profile.user_id}`,
        payload,
      );

      if (data.status === 'success') {
        setDocumentTemplates((prev) => [...prev, data.data]);
        return data.data;
      } else {
        throw new Error('An error occurred while creating document template');
      }
    },
  );
*/
  const updatePlan = useMutation(async (plan: IPlan) => {
    const { data } = await axios.put(`users/plans/${plan.id}`, { plan });
    return data;
  });

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const updateDocumentTemplate = useMutation(
    async (template: IUserDocumentTemplate) => {
      if (!profile?.user_id) throw new Error('No user profile available');

      console.log('::::Saving template:', template);

      const { data } = await axios.post(`users/document_template`, {
        template,
      });

      if (data.status === 'success') {
        console.log('::::updateDocumentTemplate:', data.data);

        if (data.data.documentTemplates) {
          setDocumentTemplates(data.data.documentTemplates);
        }

        return data.data;
      } else {
        throw new Error('An error occurred while updating document template');
      }
    },
  );

  const updateUserSettingsMutation = useMutation(
    async ({
      settings,
      services,
    }: {
      settings: IUserSettings;
      services?: number[];
    }) => {
      try {
        console.log('::::updateUserSettings:', settings);

        const { data } = await axios.put('users/settings', {
          settings,
          services,
        });

        console.log('::::updateUserSettings data:', data);

        return data;
      } catch (err: any) {
        if (err.response) {
          console.error('Server responded with an error:', err.response.data);
        } else if (err.request) {
          console.error('No response received from server:', err.request);
        } else {
          console.error('Error setting up request:', err.message);
        }
        return false;
      }
    },
  );

  const getAvailability = async () => {
    const { data } = await axios.get('users/availability');
    console.log('::::::getAvailability, data:', data);
    return data;
  };

  const fetchUser = async (user_id: number) => {
    try {
      const { data } = await axios.get(`users/${user_id}`);

      return data.data;
    } catch (e) {
      console.log('::::fetchUser, error:', e);
      return null;
    }
  };

  const userCache = useRef<{ [key: number]: IUser }>({});
  const getUser = async (user_id: number) => {
    if (userCache.current[user_id]) return userCache.current[user_id];

    userCache.current[user_id] = {
      user_id,
      email: 'pending...',
      role: 'n/a',
      sub: 'n/a',
    };

    userCache.current[user_id] = (await fetchUser(user_id)) || {
      user_id,
      email: 'n/a',
      role: 'n/a',
      sub: null,
    };

    return userCache.current[user_id];
  };

  return {
    gitHash,
    profile,
    updateProfile,
    isLoading,
    isError,
    error: updateProfileError,

    userSettings,
    organization,

    updateUserSettings: updateUserSettingsMutation.mutateAsync,
    updateUserSettingsError: updateUserSettingsMutation.error,

    plans,
    setPlans,
    updatePlan: updatePlan.mutateAsync,

    services,
    setServices,
    documentTemplates,
    setDocumentTemplates,

    //createDocumentTemplate,
    updateDocumentTemplate: updateDocumentTemplate.mutateAsync,
    getAvailability,

    getUser,
  };
};

export default useUserProfile;
